import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import { Category } from '@/types';
import { useAuth } from '../AuthContext';
import { useProfile } from '../ProfileContext';
import { categoriesReducer, initialState } from './reducer';
import { useCategorySelectors } from './selectors';
import { 
  loadCategoriesForProfile as loadCategoriesOp,
  addCategory as addCategoryOp,
  updateCategory as updateCategoryOp,
  deleteCategory as deleteCategoryOp
} from './operations';
import { logger } from '@/utils/logger';

interface CategoriesContextType {
  categories: Category[];
  addCategory: (category: Omit<Category, 'id'>) => Promise<void>;
  updateCategory: (id: string, updates: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  getCategoryById: (id: string) => Category | undefined;
  getCategoriesByType: (type: 'receita' | 'despesa') => Category[];
  isLoading: boolean;
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined);

export const CategoriesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(categoriesReducer, initialState);
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  
  // Mutex to prevent parallel calls
  const loadingRef = useRef(false);
  const lastProfileRef = useRef<string | null>(null);
  
  const selectors = useCategorySelectors(state.categories);

  // Load categories when user or profile changes
  useEffect(() => {
    if (!user || !activeProfile) {
      dispatch({ type: 'CLEAR_CATEGORIES' });
      lastProfileRef.current = null;
      return;
    }

    const profileKey = activeProfile.id || 'default';
    
    // Skip if already loading
    if (loadingRef.current) {
      logger.debug('[Categories] Load in progress, skipping');
      return;
    }
    
    // Skip if same profile with categories already loaded
    if (lastProfileRef.current === profileKey && state.categories.length > 0) {
      logger.debug('[Categories] Using cached categories');
      return;
    }

    const loadCategories = async () => {
      loadingRef.current = true;
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        const result = await loadCategoriesOp(activeProfile.type, activeProfile.id);
        dispatch({ type: 'SET_CATEGORIES', payload: result.categories });
        lastProfileRef.current = profileKey;
        logger.info('[Categories] Loaded categories', { count: result.categories.length });
      } catch (error) {
        logger.error('[Categories] Error loading categories', { error });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
        loadingRef.current = false;
      }
    };

    loadCategories();
  }, [user, activeProfile?.id]);

  const addCategory = useCallback(async (categoryData: Omit<Category, 'id'>) => {
    try {
      const newCategory = await addCategoryOp(categoryData);
      dispatch({ type: 'ADD_CATEGORY', payload: newCategory });
    } catch (error) {
      logger.error('[Categories] Error adding category', { error });
      throw error;
    }
  }, []);

  const updateCategory = useCallback(async (id: string, updates: Partial<Category>) => {
    try {
      const currentCategory = state.categories.find(c => c.id === id);
      if (!currentCategory) throw new Error('Category not found');

      const updatedCategory = await updateCategoryOp(id, updates, currentCategory);
      dispatch({ type: 'UPDATE_CATEGORY', payload: { id, updates: updatedCategory } });
    } catch (error) {
      logger.error('[Categories] Error updating category', { error });
      throw error;
    }
  }, [state.categories]);

  const deleteCategory = useCallback(async (id: string) => {
    try {
      const category = state.categories.find(c => c.id === id);
      await deleteCategoryOp(id, category);
      dispatch({ type: 'DELETE_CATEGORY', payload: id });
    } catch (error) {
      logger.error('[Categories] Error deleting category', { error });
      throw error;
    }
  }, [state.categories]);

  const value = useMemo<CategoriesContextType>(() => ({
    categories: state.categories,
    addCategory,
    updateCategory,
    deleteCategory,
    getCategoryById: selectors.getById,
    getCategoriesByType: selectors.getByType,
    isLoading: state.isLoading,
  }), [state, addCategory, updateCategory, deleteCategory, selectors]);

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  );
};

export const useCategories = () => {
  const context = useContext(CategoriesContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoriesProvider');
  }
  return context;
};
